<!--
  @component CollectionsPanel
  @description Left panel displaying collections tree and special collections
-->
<script lang="ts">
  import { createEventDispatcher, onDestroy } from 'svelte';
  import { t } from '@/i18n';
  import {
    flattenedCollections,
    selectedCollectionKey,
    expandedKeys,
    toggleExpanded,
    SPECIAL_COLLECTIONS,
    isSpecialCollection,
    loadCollections,
    selectCollection,
  } from '@stores/collectionStore';
  import {
    selectedLibrary,
    groupLibraries,
    selectGroupLibrary,
    userLibrary,
    selectUserLibrary,
  } from '@stores/libraryStore';
  import { authStore } from '@stores/authStore';
  import { zoteroService } from '@services/zoteroService';
  import { createPanelResizer } from '@/utils/panelResize';
  import type { Collection, ZoteroGroup } from '@types';
  import type { CollectionNode } from '@services/collectionActionsService';

  export let width = 250;

  const dispatch = createEventDispatcher<{
    selectCollection: { collectionKey: string };
    collectionContextMenu: { x: number; y: number; collection: Collection; isSpecial: boolean };
    resize: { width: number };
  }>();

  // Special collections config
  const specialCollections = [
    { key: 'all', icon: '📚', nameKey: 'collection.allItems' },
    { key: 'unfiled', icon: '📄', nameKey: 'collection.unfiled' },
    { key: 'trash', icon: '🗑️', nameKey: 'collection.trash' },
  ];

  // Group expansion state
  let expandedGroups: Set<number> = new Set();
  let groupCollections: Map<number, Collection[]> = new Map();
  let loadingGroups: Set<number> = new Set();

  // Panel resize
  const resizer = createPanelResizer({
    minWidth: 150,
    maxWidth: 500,
    direction: 'right',
    onResize: (newWidth) => dispatch('resize', { width: newWidth }),
  });

  function handleCollectionSelect(collectionKey: string) {
    dispatch('selectCollection', { collectionKey });
  }

  function handleContextMenu(
    event: MouseEvent,
    collection: Collection | { key: string },
    isSpecial: boolean
  ) {
    event.preventDefault();
    event.stopPropagation();

    dispatch('collectionContextMenu', {
      x: event.clientX,
      y: event.clientY,
      collection: collection as Collection,
      isSpecial,
    });
  }

  function handleToggleExpand(event: MouseEvent, key: string) {
    event.stopPropagation();
    toggleExpanded(key);
  }

  // Cleanup resize listeners on component destroy
  onDestroy(resizer.cleanup);

  function hasChildren(collection: CollectionNode): boolean {
    return collection.children && collection.children.length > 0;
  }

  async function handleGroupToggle(event: MouseEvent, group: ZoteroGroup) {
    event.stopPropagation();

    if (expandedGroups.has(group.id)) {
      // Collapse
      expandedGroups.delete(group.id);
      expandedGroups = expandedGroups; // Trigger reactivity
    } else {
      // Expand and load collections if not already loaded
      expandedGroups.add(group.id);
      expandedGroups = expandedGroups; // Trigger reactivity

      if (!groupCollections.has(group.id)) {
        loadingGroups.add(group.id);
        loadingGroups = loadingGroups;

        const result = await zoteroService.getCollections(group.id, 'group');
        if (result.success) {
          groupCollections.set(group.id, result.data);
          groupCollections = groupCollections; // Trigger reactivity
        }

        loadingGroups.delete(group.id);
        loadingGroups = loadingGroups;
      }
    }
  }

  function handleGroupCollectionSelect(group: ZoteroGroup, collectionKey: string) {
    // Switch to group library and select collection
    selectGroupLibrary(group);
    selectCollection(collectionKey);
  }

  async function handleGroupAllItems(group: ZoteroGroup) {
    selectGroupLibrary(group);
    selectCollection('all');

    // Expand the group and load collections if not already done
    if (!expandedGroups.has(group.id)) {
      expandedGroups.add(group.id);
      expandedGroups = expandedGroups;

      if (!groupCollections.has(group.id)) {
        loadingGroups.add(group.id);
        loadingGroups = loadingGroups;

        const result = await zoteroService.getCollections(group.id, 'group');
        if (result.success) {
          groupCollections.set(group.id, result.data);
          groupCollections = groupCollections;
        }

        loadingGroups.delete(group.id);
        loadingGroups = loadingGroups;
      }
    }
  }

  function handleMyLibrarySelect() {
    if ($authStore.userId) {
      selectUserLibrary($authStore.userId);
    }
  }

  // Check if user library is selected
  $: isUserLibrarySelected = $selectedLibrary?.type === 'user';
</script>

<div class="collections-panel" style="width: {width}px;">
  <div class="collections-header">
    {$selectedLibrary ? $selectedLibrary.name : $t('collection.collections')}
  </div>

  <div class="collections-content">
    <!-- My Library Section -->
    <button
      class="section-header clickable"
      class:selected={isUserLibrarySelected}
      on:click={handleMyLibrarySelect}
    >
      <span class="section-icon">📚</span>
      {$t('library.myLibrary')}
    </button>

    <!-- User Collections (only show when user library is selected) -->
    {#if isUserLibrarySelected}
      {#each $flattenedCollections as collection (collection.key)}
        <button
          class="collection-item level-{collection.level}"
          class:selected={$selectedCollectionKey === collection.key}
          on:click={() => handleCollectionSelect(collection.key)}
          on:contextmenu={(e) => handleContextMenu(e, collection, false)}
        >
          {#if hasChildren(collection)}
            <button
              class="expand-button"
              on:click={(e) => handleToggleExpand(e, collection.key)}
              aria-label={$expandedKeys.has(collection.key)
                ? $t('a11y.collapseCollection')
                : $t('a11y.expandCollection')}
            >
              {$expandedKeys.has(collection.key) ? '▼' : '▶'}
            </button>
          {:else}
            <span class="expand-placeholder"></span>
          {/if}
          <span class="collection-icon">📁</span>
          <span class="collection-name">{collection.data.name}</span>
        </button>
      {/each}

      <!-- Special Collections -->
      {#each specialCollections as special (special.key)}
        <button
          class="collection-item special"
          class:selected={$selectedCollectionKey === special.key}
          on:click={() => handleCollectionSelect(special.key)}
          on:contextmenu={(e) => handleContextMenu(e, { key: special.key }, true)}
        >
          <span class="expand-placeholder"></span>
          <span class="collection-icon">{special.icon}</span>
          <span class="collection-name">{$t(special.nameKey)}</span>
        </button>
      {/each}
    {/if}

    <!-- Shared Libraries -->
    {#if $groupLibraries.length > 0}
      <div class="section-header">{$t('library.groupLibraries')}</div>
      {#each $groupLibraries as group (group.id)}
        <button
          class="collection-item group-library"
          class:selected={$selectedLibrary?.type === 'group' &&
            $selectedLibrary?.id === group.id &&
            $selectedCollectionKey === 'all'}
          on:click={() => handleGroupAllItems(group)}
        >
          <button
            class="expand-button"
            on:click={(e) => handleGroupToggle(e, group)}
            aria-label={expandedGroups.has(group.id)
              ? $t('a11y.collapseCollection')
              : $t('a11y.expandCollection')}
          >
            {#if loadingGroups.has(group.id)}
              <span class="loading-dot">●</span>
            {:else}
              {expandedGroups.has(group.id) ? '▼' : '▶'}
            {/if}
          </button>
          <span class="collection-icon">👥</span>
          <span class="collection-name">{group.data.name}</span>
        </button>

        <!-- Group Collections (when expanded) -->
        {#if expandedGroups.has(group.id)}
          <!-- Group's regular collections -->
          {#each groupCollections.get(group.id) || [] as collection (collection.key)}
            <button
              class="collection-item group-collection"
              class:selected={$selectedLibrary?.type === 'group' &&
                $selectedLibrary?.id === group.id &&
                $selectedCollectionKey === collection.key}
              on:click={() => handleGroupCollectionSelect(group, collection.key)}
            >
              <span class="expand-placeholder"></span>
              <span class="collection-icon">📁</span>
              <span class="collection-name">{collection.data.name}</span>
            </button>
          {/each}

          <!-- Group's special collections -->
          {#each specialCollections as special (special.key)}
            <button
              class="collection-item group-collection special"
              class:selected={$selectedLibrary?.type === 'group' &&
                $selectedLibrary?.id === group.id &&
                $selectedCollectionKey === special.key}
              on:click={() => handleGroupCollectionSelect(group, special.key)}
            >
              <span class="expand-placeholder"></span>
              <span class="collection-icon">{special.icon}</span>
              <span class="collection-name">{$t(special.nameKey)}</span>
            </button>
          {/each}
        {/if}
      {/each}
    {/if}
  </div>

  <!-- Resize Handle -->
  <div
    class="resize-handle"
    on:mousedown={resizer.start}
    role="presentation"
    aria-hidden="true"
  ></div>
</div>

<style>
  .collections-panel {
    background: var(--color-bg-secondary, #383838);
    border-right: 1px solid var(--color-border, #555);
    display: flex;
    flex-direction: column;
    position: relative;
    min-width: 150px;
    max-width: 500px;
  }

  .collections-header {
    padding: 12px;
    background: var(--color-bg-tertiary, #3c3c3c);
    border-bottom: 1px solid var(--color-border, #555);
    font-weight: 600;
    font-size: 13px;
    flex-shrink: 0;
  }

  .collections-content {
    flex: 1;
    overflow-y: auto;
  }

  .collection-item {
    width: 100%;
    padding: 8px 12px;
    cursor: pointer;
    border: none;
    border-bottom: 1px solid var(--color-border-light, #444);
    font-size: 13px;
    display: flex;
    align-items: center;
    gap: 6px;
    transition: background-color 0.2s;
    user-select: none;
    background: transparent;
    color: var(--color-text-primary, #e8e8e8);
    text-align: left;
  }

  .collection-item.level-1 {
    padding-left: 24px;
  }

  .collection-item.level-2 {
    padding-left: 40px;
  }

  .collection-item.level-3 {
    padding-left: 56px;
  }

  .collection-item:hover {
    background: var(--color-bg-hover, #444);
  }

  .collection-item.selected {
    background: var(--color-accent, #5c7cfa);
    color: white;
  }

  .collection-item.special {
    color: var(--color-text-secondary, #ccc);
    font-style: italic;
  }

  .expand-button {
    width: 16px;
    height: 16px;
    padding: 0;
    border: none;
    background: transparent;
    color: var(--color-text-secondary, #aaa);
    cursor: pointer;
    font-size: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .expand-button:hover {
    color: var(--color-text-primary, #e8e8e8);
  }

  .expand-placeholder {
    width: 16px;
    flex-shrink: 0;
  }

  .collection-icon {
    font-size: 14px;
    flex-shrink: 0;
  }

  .collection-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
  }

  .section-header {
    padding: 8px 12px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--color-text-muted, #888);
    background: var(--color-bg-tertiary, #3c3c3c);
    border: none;
    border-top: 1px solid var(--color-border, #555);
    border-bottom: 1px solid var(--color-border, #555);
    margin-top: 8px;
    width: 100%;
    text-align: left;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .section-header:first-child {
    margin-top: 0;
  }

  .section-header.clickable {
    cursor: pointer;
    transition: background-color 0.2s;
  }

  .section-header.clickable:hover {
    background: var(--color-bg-hover, #444);
  }

  .section-header.clickable.selected {
    background: var(--color-accent, #5c7cfa);
    color: white;
  }

  .section-icon {
    font-size: 14px;
  }

  .collection-item.group-library {
    color: var(--color-text-secondary, #ccc);
  }

  .collection-item.group-collection {
    padding-left: 32px;
    color: var(--color-text-secondary, #ccc);
  }

  .loading-dot {
    animation: pulse 1s ease-in-out infinite;
    color: var(--color-accent, #5c7cfa);
  }

  @keyframes pulse {
    0%,
    100% {
      opacity: 0.4;
    }
    50% {
      opacity: 1;
    }
  }

  .resize-handle {
    position: absolute;
    top: 0;
    right: 0;
    width: 4px;
    height: 100%;
    cursor: ew-resize;
    background: transparent;
    z-index: 10;
    transition: background-color 0.2s;
  }

  .resize-handle:hover {
    background: var(--color-accent, #5c7cfa);
  }

  /* ==========================================================================
     Responsive Styles
     ========================================================================== */

  /* Mobile: Full height overlay */
  @media (max-width: 767px) {
    .collections-panel {
      min-width: 0;
      max-width: none;
      border-right: none;
    }

    .resize-handle {
      display: none;
    }

    .collections-header {
      padding: 14px 16px;
      font-size: 14px;
    }

    .collection-item {
      padding: 12px 16px;
      min-height: 48px;
    }

    .section-header {
      padding: 10px 16px;
    }
  }

  /* Touch-friendly targets */
  @media (pointer: coarse) {
    .collection-item {
      min-height: 48px;
    }

    .expand-button {
      width: 32px;
      height: 32px;
      font-size: 12px;
    }
  }
</style>
